import { useCallback, useEffect, useRef } from "react";

import {
  parseChromecastServiceScheduleSubtitlesItem,
  parseChromecastSubtitlesItem
} from "../../components/player-ui/helpers";
import { usePrevious } from "../../helpers/commonUtils";
import { usePlayerStore } from "../../store/usePlayerStore";
import { AudioTrack } from "../../types/common";
import { ChromecastServiceSchedule, Subtitles } from "../../types/player-api";
import {
  areSameTracks,
  resolveActiveCastTracks,
  resolveTrackStyleChanges
} from "../useChromecast/chromecast-helpers";

export const useChromecastTracks = ({
  remotePlayer,
  remotePlayerController
}: {
  remotePlayer: cast.framework.RemotePlayer | undefined;
  remotePlayerController: cast.framework.RemotePlayerController | undefined;
}): void => {
  const { getState, useStoreState } = usePlayerStore();

  const isCasting = useStoreState.isCasting();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();
  const selectedSubtitles = useStoreState.selectedSubtitles();
  const subtitlesBackground = useStoreState.subtitlesBackground();
  const subtitlesSizeMultiplier = useStoreState.subtitlesSizeMultiplier();

  const setAudioTracks = useStoreState.setAudioTracks();
  const setSubtitles = useStoreState.setSubtitles();

  const audioTracksBeforeCasting = useRef<AudioTrack[] | undefined>(undefined);

  const prevIsCasting = usePrevious(isCasting);

  const setSubtitlesIfChanged = useCallback(
    (newSubtitles: Subtitles[]) => {
      if (!areSameTracks(getState().subtitles, newSubtitles)) {
        setSubtitles(newSubtitles);
      }
    },
    [getState, setSubtitles]
  );

  const setAudioTracksIfChanged = useCallback(
    (newAudioTracks: AudioTrack[]) => {
      if (!areSameTracks(getState().audioTracks, newAudioTracks)) {
        setAudioTracks(newAudioTracks);
      }
    },
    [getState, setAudioTracks]
  );

  const setTracksFromServiceSchedule = useCallback(
    (serviceSchedule: ChromecastServiceSchedule) => {
      setAudioTracksIfChanged(
        serviceSchedule.audioTracks.map(a => ({
          id: a.trackIndex,
          label: a.label,
          language: a.language,
          type: a.type
        }))
      );
      setSubtitlesIfChanged(
        serviceSchedule.subtitles.map(
          parseChromecastServiceScheduleSubtitlesItem
        )
      );
    },
    [setAudioTracksIfChanged, setSubtitlesIfChanged]
  );

  const setTracksFromMediaInfo = useCallback(
    (mediaInfo: chrome.cast.media.MediaInfo) => {
      setAudioTracksIfChanged(
        Array.from(mediaInfo?.tracks || [])
          .filter(track => track.type === "AUDIO")
          .map(track => ({
            id: track.trackId,
            label: track.name,
            language: track.language
          }))
      );

      setSubtitlesIfChanged(
        Array.from(mediaInfo?.tracks || [])
          .filter(track => ["SUBTITLE", "CAPTION"].includes(track.subtype))
          .map(parseChromecastSubtitlesItem)
      );
    },
    [setAudioTracksIfChanged, setSubtitlesIfChanged]
  );

  const mediaInfoChanged = useCallback(
    ({ value }: cast.framework.RemotePlayerChangedEvent) => {
      try {
        const serviceScheduleStr = value?.customData?.serviceSchedule;
        const serviceSchedule: ChromecastServiceSchedule | undefined =
          serviceScheduleStr &&
          (JSON.parse(serviceScheduleStr) as ChromecastServiceSchedule);

        if (serviceSchedule) {
          setTracksFromServiceSchedule(serviceSchedule);
        } else {
          setTracksFromMediaInfo(value);
        }
      } catch (err) {
        console.error("Failed to get tracks from event data", err);
      }
    },
    [setTracksFromMediaInfo, setTracksFromServiceSchedule]
  );

  const isMediaLoadedChanged = useCallback(
    ({ value }: cast.framework.RemotePlayerChangedEvent) => {
      if (value) {
        setTracksFromMediaInfo(value);
        resolveTrackStyleChanges(subtitlesBackground, subtitlesSizeMultiplier);
      }
    },
    [setTracksFromMediaInfo, subtitlesBackground, subtitlesSizeMultiplier]
  );

  useEffect(() => {
    if (remotePlayer && isCasting) {
      resolveTrackStyleChanges(subtitlesBackground, subtitlesSizeMultiplier);
    }
  }, [isCasting, remotePlayer, subtitlesBackground, subtitlesSizeMultiplier]);

  useEffect(() => {
    if (remotePlayer && isCasting) {
      if (
        !audioTracksBeforeCasting.current &&
        !getState().media?.serviceSchedule
      ) {
        // If serviceSchedule is used, the audio tracks will update from the serviceSchedule after casting ends
        // But if serviceSchedule is not used, the audio tracks will not update and need to be restored manually
        audioTracksBeforeCasting.current = getState().audioTracks;
      }
      resolveActiveCastTracks(
        remotePlayer,
        selectedAudioTrackId,
        selectedSubtitles
      ).catch(err => {
        console.error("Failed to resolve cast tracks", err);
      });
    }
  }, [
    isCasting,
    useStoreState,
    remotePlayer,
    selectedAudioTrackId,
    selectedSubtitles,
    getState
  ]);

  useEffect(() => {
    if (!isCasting && prevIsCasting && audioTracksBeforeCasting.current) {
      setAudioTracks(audioTracksBeforeCasting.current);
      audioTracksBeforeCasting.current = undefined;
    }
  }, [isCasting, prevIsCasting, setAudioTracks]);

  useEffect(() => {
    if (!remotePlayerController || !isCasting) return;

    const events = [
      {
        event: cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
        handler: mediaInfoChanged
      },
      {
        event: cast.framework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED,
        handler: isMediaLoadedChanged
      }
    ];

    events.forEach(({ event, handler }) => {
      remotePlayerController.addEventListener(event, handler);
    });

    return () => {
      events.forEach(({ event, handler }) => {
        remotePlayerController.removeEventListener(event, handler);
      });
    };
  }, [
    isCasting,
    isMediaLoadedChanged,
    mediaInfoChanged,
    remotePlayerController
  ]);
};
